import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import SimpleListbox, { SimpleOption } from "../../../../components/input/SimpleListbox";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { WritingUsage, WritingUsageByWriter } from "./UsageFilteredByCourseViewModel";
import { studentUsageLink } from "./UsageReport";

interface WritingUsageChartData {
	name: string;
	value: number;
	link: string;
}

function initializeOptions(
	label: string,
	chartData: WritingUsageChartData[],
	average: number
): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			top: 20,
			left: 10,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: label,
			nameLocation: "middle",
			nameGap: 30,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: chartData.map((d) => d.name),
			inverse: true,
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: (params: { value: string; name: string; color: string }) => {
				const heading = `<div>${params.name}</div>`;
				const lineItem = `<div>${tooltipMark(params.color)} ${label}: <b>${params.value}</b></div>`;
				const avgLine = `<div>${tooltipMark("red")} Class Average: <b>${average}</b></div>`;
				return heading + lineItem + avgLine;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [
			{
				type: "bar",
				id: "category",
				name: label,
				data: chartData.map((d) => ({ value: d.value, link: d.link })),
				itemStyle: {
					color: function (param: { dataIndex: number }) {
						return chartColors[param.dataIndex % chartColors.length];
					},
				},
				label: {
					show: true,
					position: ["100%", "50%"],
					height: 25,
					width: 25,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 25,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 12,
				},
				barMinWidth: 40,
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
					},
				},
				universalTransition: {
					enabled: true,
					divideShape: "clone",
				},
			},
			{
				type: "bar",
				id: "categoryAverage",
				data: [average],
				barWidth: 0.1,
				barGap: 0.1,
				color: "transparent",
				markLine: {
					show: true,
					data: [
						{
							name: "Class Average",
							type: "average",
						},
					],
					label: {
						show: false,
					},
					lineStyle: {
						color: "red",
					},
					symbol: ["circle", "circle"],
					silent: true,
				},
			},
		],
	};
	return option;
}

interface WritingUsageChartProps {
	title: string;
	label: string;
	data: WritingUsageChartData[];
	average: number;
}

export function WritingUsageChart(props: WritingUsageChartProps) {
	const chartRef = useRef<ReactEcharts>(null);
	const navigate = useNavigate();

	const options = initializeOptions(props.label, props.data, props.average);
	const chartHeight = props.data.length * 55 + 64;

	const onChartClick = (params: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	useEffect(() => {
		// the event handler ReactEcharts provides interferes with
		// transitions when data in the chart changes.
		if (chartRef?.current) {
			const instance = chartRef.current.getEchartsInstance();
			instance.on("click", onChartClick);
		}
	}, [chartRef]);

	if (!props.data) return "";

	return (
		<ReactEcharts
			ref={chartRef}
			option={options}
			className="w-full pl-6"
			style={{
				height: chartHeight,
			}}
		/>
	);
}

const options: SimpleOption[] = [
	{ value: "numberOfDrafts", label: "Number of Drafts" },
	{ value: "numberOfEssays", label: "Number of Essays" },
	{ value: "draftsPerEssay", label: "Number of Drafts per Essay" },
];

export function WritingUsageChartWrapper(props: {
	className?: string;
	courseId: number;
	title: string;
	writerData: WritingUsageByWriter[];
	classData: WritingUsage;
}) {
	const getDraftCounts = () =>
		props.writerData.map((d) => ({
			name: d.writerName,
			value: d.draftCount,
			link: studentUsageLink(props.courseId, d.writerId),
		}));

	const getEssayCounts = () =>
		props.writerData.map((d) => ({
			name: d.writerName,
			value: d.essayCount,
			link: studentUsageLink(props.courseId, d.writerId),
		}));

	const getAverages = () =>
		props.writerData.map((d) => ({
			name: d.writerName,
			value: d.draftPerEssayAverageCount,
			link: studentUsageLink(props.courseId, d.writerId),
		}));

	const [selected, setSelected] = useState("numberOfDrafts");
	const [label, setLabel] = useState("Number of Drafts");
	const [data, setData] = useState(getDraftCounts());
	const [average, setAverage] = useState(props.classData.draftCount);

	useEffect(() => {
		const option = options.find((o) => o.value === selected) ?? options[0];

		setLabel(option!.label);
		switch (option?.value) {
			case "numberOfDrafts":
				setData(getDraftCounts());
				setAverage(props.classData.draftCount);
				break;
			case "numberOfEssays":
				setData(getEssayCounts());
				setAverage(props.classData.essayCount);
				break;
			case "draftsPerEssay":
				setData(getAverages());
				setAverage(props.classData.draftPerEssayAverageCount);
				break;
		}
	}, [selected]);

	if (!props.writerData || props.writerData.length == 0) return "";

	return (
		<div>
			<SimpleListbox
				id="viewFilter"
				options={options}
				value={selected}
				className={`${props.className} w-1/4`}
				onChange={(e) => setSelected(e.target.value)}
			/>

			<WritingUsageChart title={props.title} label={label} data={data} average={average} />
		</div>
	);
}
